import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Employee, Attendance, AttendanceStatus } from '../lib/supabase';

const STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Presente', late: 'Tarde', absent: 'Falta',
  vacation: 'Vacaciones', sick_leave: 'Descanso Médico', permission: 'Permiso',
};
const STATUS_COLORS: Record<AttendanceStatus, string> = {
  present: 'badge-neutral', late: 'badge-warning', absent: 'badge-error',
  vacation: 'badge-info', sick_leave: 'badge-info', permission: 'badge-warning',
};

// ════════════════════════════════════════════════════════════════════════════
// HELPERS DE HORARIO
// Resuelve el horario efectivo de un empleado:
//   Si el empleado tiene shift_start/shift_end → usa esos.
//   Si no → hereda los de company_settings.
// ════════════════════════════════════════════════════════════════════════════
interface EffectiveSchedule {
  checkIn: string;        // HH:MM
  checkOut: string;       // HH:MM
  tolerance: number;      // minutos
  breakStart: string;     // HH:MM o ''
  breakEnd: string;       // HH:MM o ''
  breakMinutes: number;   // minutos de refrigerio
  shiftType: string;      // 'full' | 'morning' | 'afternoon' | 'empresa'
  effectiveHours: number; // horas laborables reales (descontando refrigerio)
}

function parseHHMM(t: string): [number, number] {
  if (!t || !t.includes(':')) return [0, 0];
  const parts = t.split(':').map(Number);
  return [isNaN(parts[0]) ? 0 : parts[0], isNaN(parts[1]) ? 0 : parts[1]];
}

/**
 * Normaliza cualquier formato de hora a 'HH:MM:SS' para PostgreSQL.
 * '08:00'        → '08:00:00'
 * '08:00:00'     → '08:00:00'  (sin cambio)
 * '08:00:00:00'  → '08:00:00'  (corrige doble :00)
 */
function toTimeDB(t: string): string {
  if (!t) return '00:00:00';
  const parts = t.split(':').map(s => s.padStart(2, '0'));
  // Tomar solo HH, MM, SS (máx 3 partes)
  const hh = parts[0] || '00';
  const mm = parts[1] || '00';
  const ss = parts[2]?.length === 2 ? parts[2] : '00';
  return `${hh}:${mm}:${ss}`;
}

function getEffectiveSchedule(
  emp: Employee,
  companySettings: { check_in_time: string; check_out_time: string; tolerance_minutes: number }
): EffectiveSchedule {
  // Usar horario del empleado si está completo y válido, si no usar empresa
  const checkIn  = (emp.shift_start && emp.shift_start.includes(':'))
    ? emp.shift_start : companySettings.check_in_time;
  const checkOut = (emp.shift_end && emp.shift_end.includes(':'))
    ? emp.shift_end : companySettings.check_out_time;
  const tolerance = (emp.tolerance_minutes != null && !isNaN(emp.tolerance_minutes))
    ? emp.tolerance_minutes : companySettings.tolerance_minutes;
  const breakStart = (emp.shift_type === 'full' && emp.break_start) ? emp.break_start : '';
  const breakEnd   = (emp.shift_type === 'full' && emp.break_end)   ? emp.break_end   : '';

  // Minutos de refrigerio
  let breakMinutes = 0;
  if (breakStart && breakEnd) {
    const [bsh, bsm] = parseHHMM(breakStart);
    const [beh, bem] = parseHHMM(breakEnd);
    breakMinutes = Math.max(0, (beh * 60 + bem) - (bsh * 60 + bsm));
  }

  // Horas efectivas (protegido contra NaN)
  const [sh, sm] = parseHHMM(checkIn);
  const [eh, em] = parseHHMM(checkOut);
  const totalMins = (eh * 60 + em) - (sh * 60 + sm);
  const netMins   = Math.max(0, totalMins - breakMinutes);
  const effectiveHours = Math.round((netMins / 60) * 100) / 100;

  return {
    checkIn, checkOut, tolerance, breakStart, breakEnd,
    breakMinutes,
    effectiveHours: isNaN(effectiveHours) ? 8 : effectiveHours,
    shiftType: emp.shift_type || 'empresa',
  };
}

/** Minutos de tardanza comparando check_in real vs horario esperado */
function calcLateMinutes(actualCheckIn: string, schedule: EffectiveSchedule): number {
  if (!actualCheckIn) return 0;
  const [eh, em] = parseHHMM(schedule.checkIn);
  const [ch, cm] = parseHHMM(actualCheckIn);
  const result = (ch * 60 + cm) - (eh * 60 + em + schedule.tolerance);
  return Math.max(0, isNaN(result) ? 0 : result);
}

/**
 * Horas trabajadas reales = (check_out - check_in) - refrigerio
 */
function calcHoursWorked(checkIn: string, checkOut: string, schedule: EffectiveSchedule): number {
  if (!checkIn || !checkOut) return 0;
  const [ih, im] = parseHHMM(checkIn);
  const [oh, om] = parseHHMM(checkOut);
  const totalMins = (oh * 60 + om) - (ih * 60 + im);
  const netMins   = Math.max(0, totalMins - schedule.breakMinutes);
  const result    = Math.round((netMins / 60) * 100) / 100;
  return isNaN(result) ? 0 : result;
}

function calcOvertimeMinutes(hoursWorked: number, schedule: EffectiveSchedule): number {
  return Math.max(0, Math.round((hoursWorked - schedule.effectiveHours) * 60));
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL DE EDICIÓN DE HORA
// ════════════════════════════════════════════════════════════════════════════
interface EditModalProps {
  employee: Employee;
  record: Attendance | null;
  date: string;
  schedule: EffectiveSchedule;
  company_id: string;
  onClose: () => void;
  onSaved: (msg: string) => void;
}

function EditTimeModal({ employee, record, date, schedule, company_id, onClose, onSaved }: EditModalProps) {
  const [checkIn,  setCheckIn]  = useState(record?.check_in?.slice(0, 5)  || '');
  const [checkOut, setCheckOut] = useState(record?.check_out?.slice(0, 5) || '');
  const [status,   setStatus]   = useState<AttendanceStatus>(
    (record?.status as AttendanceStatus) || 'present'
  );
  const [saving, setSaving] = useState(false);

  function liveAutoStatus(ci: string): AttendanceStatus {
    return calcLateMinutes(ci, schedule) > 0 ? 'late' : 'present';
  }

  function handleCheckInChange(val: string) {
    setCheckIn(val);
    if (val) setStatus(liveAutoStatus(val));
  }

  const liveMinutes  = calcLateMinutes(checkIn, schedule);
  const liveHours    = calcHoursWorked(checkIn, checkOut, schedule);
  const liveOvertime = calcOvertimeMinutes(liveHours, schedule);

  async function handleSave() {
    setSaving(true);
    const lateMinutes     = calcLateMinutes(checkIn, schedule);
    const hoursWorked     = calcHoursWorked(checkIn, checkOut, schedule);
    const overtimeMinutes = calcOvertimeMinutes(hoursWorked, schedule);
    const finalStatus     = checkIn ? liveAutoStatus(checkIn) : status;

    const payload: any = {
      company_id,
      employee_id: employee.id,
      date,
      check_in:  checkIn  ? toTimeDB(checkIn) : null,
      check_out: checkOut ? toTimeDB(checkOut) : null,
      late_minutes: lateMinutes,
      hours_worked: hoursWorked,
      overtime_minutes: overtimeMinutes,
      status: checkIn ? finalStatus : status,
      expected_check_in:  toTimeDB(schedule.checkIn),
      expected_check_out: toTimeDB(schedule.checkOut),
    };

    if (record?.id) {
      await supabase.from('attendance').update(payload).eq('id', record.id);
    } else {
      await supabase.from('attendance').insert(payload);
    }
    setSaving(false);
    onSaved(`Asistencia guardada · ${checkIn || '—'} → ${checkOut || '—'}${lateMinutes > 0 ? ` · ${lateMinutes} min tarde` : ''}`);
    onClose();
  }

  const shiftLabels: Record<string, string> = {
    full: 'Turno completo', morning: 'Turno mañana',
    afternoon: 'Turno tarde', empresa: 'Horario empresa',
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-md animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-outline-variant/10">
          <div>
            <h2 className="text-lg font-bold font-headline">
              {record ? 'Editar asistencia' : 'Registrar asistencia'}
            </h2>
            <p className="text-xs text-on-surface-variant mt-0.5">
              {employee.first_name} {employee.last_name} · {date}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-container-high rounded-lg">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Horario del empleado */}
          <div className="p-3 bg-primary/5 rounded-xl border border-primary/15 text-xs flex items-start gap-2">
            <span className="material-symbols-outlined text-primary text-[16px] mt-0.5">schedule</span>
            <div className="flex-1">
              <span className="font-bold text-on-surface">
                {shiftLabels[schedule.shiftType] || 'Horario empresa'}:
              </span>
              <span className="text-on-surface ml-1">
                {schedule.checkIn} → {schedule.checkOut}
              </span>
              {schedule.breakMinutes > 0 && (
                <span className="text-amber-700 ml-2">
                  · refrigerio {schedule.breakStart}–{schedule.breakEnd} ({schedule.breakMinutes} min)
                </span>
              )}
              <span className="text-on-surface-variant ml-2">
                · {schedule.effectiveHours}h efectivas · tolerancia {schedule.tolerance} min
              </span>
            </div>
          </div>

          {/* Entrada */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              Hora de entrada
            </label>
            <div className="flex items-center gap-3">
              <input
                type="time"
                value={checkIn}
                onChange={e => handleCheckInChange(e.target.value)}
                className="input-base flex-1"
              />
              {checkIn && liveMinutes > 0 && (
                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-lg whitespace-nowrap">
                  +{liveMinutes} min tarde
                </span>
              )}
              {checkIn && liveMinutes === 0 && (
                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">✓ A tiempo</span>
              )}
            </div>
          </div>

          {/* Salida */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
              Hora de salida
            </label>
            <div className="flex items-center gap-3">
              <input
                type="time"
                value={checkOut}
                onChange={e => setCheckOut(e.target.value)}
                className="input-base flex-1"
              />
              {liveHours > 0 && (
                <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded-lg whitespace-nowrap">
                  {liveHours}h trabajadas
                </span>
              )}
            </div>
          </div>

          {/* Estado */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Estado</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as AttendanceStatus)}
              className="input-base"
            >
              {(Object.keys(STATUS_LABELS) as AttendanceStatus[]).map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>

          {/* Resumen en vivo */}
          {(checkIn || checkOut) && (
            <div className="grid grid-cols-3 gap-3 p-4 bg-surface-container-low rounded-xl">
              <div className="text-center">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold">Tardanza</p>
                <p className={`text-lg font-black ${liveMinutes > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {liveMinutes} min
                </p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold">Horas netas</p>
                <p className="text-lg font-black text-primary">{liveHours}h</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-on-surface-variant uppercase font-bold">Sobretiempo</p>
                <p className="text-lg font-black text-secondary">{liveOvertime} min</p>
              </div>
            </div>
          )}

          {/* Nota refrigerio si aplica */}
          {schedule.breakMinutes > 0 && checkIn && checkOut && (
            <p className="text-[10px] text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
              ⚠ Se descuentan {schedule.breakMinutes} min de refrigerio automáticamente del total de horas.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-outline-variant/10">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg border border-outline-variant/30 text-sm font-bold text-on-surface-variant hover:bg-surface-container-low transition-all">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary text-white py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-60">
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL DE ASISTENCIA
// ════════════════════════════════════════════════════════════════════════════
export function AttendancePage() {
  const { company } = useAuth();
  const [employees,    setEmployees]    = useState<Employee[]>([]);
  const [attendance,   setAttendance]   = useState<Attendance[]>([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState<string | null>(null);
  const [toast,        setToast]        = useState<string | null>(null);
  const [editModal,    setEditModal]    = useState<{ employee: Employee; record: Attendance | null } | null>(null);
  const [companySettings, setCompanySettings] = useState({
    check_in_time: '08:00', check_out_time: '17:00', tolerance_minutes: 5,
  });

  useEffect(() => { if (company) { loadData(); loadSettings(); } }, [company, selectedDate]);

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(null), 4000); }

  async function loadSettings() {
    // maybeSingle() devuelve null sin error 406 cuando no hay filas
    const { data } = await supabase
      .from('company_settings').select('*')
      .eq('company_id', company!.id)
      .maybeSingle();
    if (data) {
      setCompanySettings({
        check_in_time:     data.check_in_time?.slice(0, 5)  || '08:00',
        check_out_time:    data.check_out_time?.slice(0, 5) || '17:00',
        tolerance_minutes: data.tolerance_minutes || 5,
      });
    } else {
      // No hay settings — crear con defaults
      await supabase.from('company_settings').upsert({
        company_id:        company!.id,
        check_in_time:     '08:00:00',
        check_out_time:    '17:00:00',
        tolerance_minutes: 5,
        overtime_rate:     1.25,
        overtime_rate_sunday: 2.00,
        currency:          'PEN',
        timezone:          'America/Lima',
      }, { onConflict: 'company_id' });
      // Mantener defaults del estado
    }
  }

  async function loadData() {
    setLoading(true);
    const [{ data: emps }, { data: att }] = await Promise.all([
      supabase.from('employees').select('*')
        .eq('company_id', company!.id).eq('active', true).order('last_name'),
      supabase.from('attendance').select('*')
        .eq('company_id', company!.id).eq('date', selectedDate),
    ]);
    setEmployees(emps || []);
    setAttendance(att || []);
    setLoading(false);
  }

  function getRecord(employeeId: string): Attendance | undefined {
    return attendance.find(a => a.employee_id === employeeId);
  }

  function getSchedule(emp: Employee): EffectiveSchedule {
    return getEffectiveSchedule(emp, companySettings);
  }

  // Registro rápido con hora actual
  async function registerCheckIn(emp: Employee) {
    setSaving(emp.id);
    const now      = format(new Date(), 'HH:mm');
    const schedule = getSchedule(emp);
    const late     = calcLateMinutes(now, schedule);
    const st: AttendanceStatus = late > 0 ? 'late' : 'present';
    const existing = getRecord(emp.id);

    if (existing) {
      await supabase.from('attendance').update({
        check_in: toTimeDB(now), late_minutes: late, status: st,
        expected_check_in: toTimeDB(schedule.checkIn),
      }).eq('id', existing.id);
    } else {
      await supabase.from('attendance').insert({
        company_id:        company!.id,
        employee_id:       emp.id,
        date:              selectedDate,
        check_in:          toTimeDB(now),
        late_minutes:      isNaN(late) ? 0 : late,
        status:            st,
        hours_worked:      0,
        overtime_minutes:  0,
        expected_check_in:  toTimeDB(schedule.checkIn),
        expected_check_out: toTimeDB(schedule.checkOut),
      });
    }
    showToast(`Entrada ${emp.first_name}: ${now}${late > 0 ? ` (+${late} min tarde)` : ' ✓'}`);
    setSaving(null);
    loadData();
  }

  async function registerCheckOut(emp: Employee) {
    setSaving(emp.id);
    const now      = format(new Date(), 'HH:mm');
    const record   = getRecord(emp.id);
    const schedule = getSchedule(emp);
    if (!record) { setSaving(null); return; }

    const hoursWorked     = calcHoursWorked(record.check_in?.slice(0, 5) || '', now, schedule);
    const overtimeMinutes = calcOvertimeMinutes(hoursWorked, schedule);

    await supabase.from('attendance').update({
      check_out: toTimeDB(now), hours_worked: hoursWorked,
      overtime_minutes: overtimeMinutes,
      expected_check_out: toTimeDB(schedule.checkOut),
    }).eq('id', record.id);

    showToast(`Salida ${emp.first_name}: ${now} · ${hoursWorked}h netas`);
    setSaving(null);
    loadData();
  }

  async function setStatus(emp: Employee, status: AttendanceStatus) {
    setSaving(emp.id);
    const existing = getRecord(emp.id);
    const schedule = getSchedule(emp);

    if (existing) {
      await supabase.from('attendance').update({
        status, late_minutes: 0,
        hours_worked: status === 'absent' ? 0 : schedule.effectiveHours,
      }).eq('id', existing.id);
    } else {
      const safeHours = (status === 'absent') ? 0
        : (isNaN(schedule.effectiveHours) ? 8 : Math.max(0, schedule.effectiveHours));
      await supabase.from('attendance').insert({
        company_id:        company!.id,
        employee_id:       emp.id,
        date:              selectedDate,
        status,
        late_minutes:      0,
        hours_worked:      parseFloat(safeHours.toFixed(2)),
        overtime_minutes:  0,
        expected_check_in:  toTimeDB(schedule.checkIn),
        expected_check_out: toTimeDB(schedule.checkOut),
      });
    }
    showToast(`${emp.first_name} → ${STATUS_LABELS[status]}`);
    setSaving(null);
    loadData();
  }

  const totalPresent = attendance.filter(a => ['present','late'].includes(a.status)).length;
  const totalAbsent  = attendance.filter(a => a.status === 'absent').length;
  const totalLate    = attendance.filter(a => a.status === 'late').length;

  const shiftBadge = (emp: Employee) => {
    if (!emp.shift_type) return null;
    const labels: Record<string, { label: string; color: string }> = {
      full:      { label: 'Completo',  color: 'bg-blue-50 text-blue-700' },
      morning:   { label: 'Mañana',    color: 'bg-amber-50 text-amber-700' },
      afternoon: { label: 'Tarde',     color: 'bg-purple-50 text-purple-700' },
    };
    const b = labels[emp.shift_type];
    if (!b) return null;
    return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${b.color}`}>{b.label}</span>;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in bg-green-600 text-white px-5 py-3 rounded-xl shadow-xl text-sm font-bold flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]">check_circle</span>{toast}
        </div>
      )}

      {/* Edit modal */}
      {editModal && (
        <EditTimeModal
          employee={editModal.employee}
          record={editModal.record}
          date={selectedDate}
          schedule={getSchedule(editModal.employee)}
          company_id={company!.id}
          onClose={() => setEditModal(null)}
          onSaved={msg => { loadData(); showToast(msg); }}
        />
      )}

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Asistencia</h1>
          <p className="text-on-surface-variant mt-1">Registro diario · Cada empleado usa su propio horario configurado</p>
        </div>
        <input
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          className="input-base w-auto"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total empleados', value: employees.length,  color: 'text-primary',   icon: 'group' },
          { label: 'Presentes',       value: totalPresent,       color: 'text-green-600', icon: 'check_circle' },
          { label: 'Tardanzas',       value: totalLate,          color: 'text-amber-600', icon: 'schedule' },
          { label: 'Faltas',          value: totalAbsent,        color: 'text-error',     icon: 'cancel' },
        ].map(s => (
          <div key={s.label} className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{s.label}</span>
              <span className={`material-symbols-outlined ${s.color} text-[20px]`}>{s.icon}</span>
            </div>
            <p className={`text-3xl font-black ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Horario empresa */}
      <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex items-center justify-between text-sm">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-primary">corporate_fare</span>
          <span className="text-on-surface-variant">
            Horario empresa por defecto:
            <strong className="text-on-surface mx-1">{companySettings.check_in_time} → {companySettings.check_out_time}</strong>
            · Tolerancia: <strong className="text-on-surface">{companySettings.tolerance_minutes} min</strong>
          </span>
        </div>
        <a href="/settings" className="text-xs font-bold text-primary hover:underline flex items-center gap-1">
          <span className="material-symbols-outlined text-[14px]">edit</span>
          Cambiar
        </a>
      </div>

      {/* Tabla */}
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-outline-variant/10 flex items-center justify-between">
          <h3 className="font-bold text-on-surface font-headline">
            {format(new Date(selectedDate + 'T12:00:00'), "EEEE d 'de' MMMM, yyyy", { locale: es })}
          </h3>
          <span className="text-xs text-on-surface-variant flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">info</span>
            Cada empleado puede tener horario y turno distinto
          </span>
        </div>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-container-low">
                {['Empleado', 'Turno / Horario', 'Estado', 'Entrada', 'Salida', 'Horas', 'Tardanza', 'Acciones'].map(h => (
                  <th key={h} className="px-4 py-3 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {employees.map(emp => {
                const record    = getRecord(emp.id);
                const schedule  = getSchedule(emp);
                const isLoading = saving === emp.id;

                return (
                  <tr key={emp.id} className="hover:bg-surface-container-low/30 transition-colors group">
                    {/* Empleado */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs flex-shrink-0">
                          {emp.first_name[0]}{emp.last_name[0]}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-on-surface leading-tight">
                            {emp.first_name} {emp.last_name}
                          </p>
                          <p className="text-xs text-on-surface-variant">{emp.position}</p>
                        </div>
                      </div>
                    </td>

                    {/* Turno + horario */}
                    <td className="px-4 py-4">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5">
                          {shiftBadge(emp)}
                          <span className="text-xs font-mono text-on-surface">
                            {schedule.checkIn}–{schedule.checkOut}
                          </span>
                        </div>
                        {schedule.breakMinutes > 0 && (
                          <span className="text-[10px] text-amber-600">
                            🍽 {schedule.breakStart}–{schedule.breakEnd} ({schedule.breakMinutes}min)
                          </span>
                        )}
                        <span className="text-[10px] text-on-surface-variant">
                          {schedule.effectiveHours}h · tol. {schedule.tolerance}min
                        </span>
                      </div>
                    </td>

                    {/* Estado */}
                    <td className="px-4 py-4">
                      {record
                        ? <span className={STATUS_COLORS[record.status as AttendanceStatus]}>{STATUS_LABELS[record.status as AttendanceStatus]}</span>
                        : <span className="text-xs text-outline-variant italic">Sin registro</span>
                      }
                    </td>

                    {/* Entrada (clicable) */}
                    <td className="px-4 py-4">
                      <span
                        className="text-sm font-mono cursor-pointer hover:text-primary hover:underline"
                        onClick={() => setEditModal({ employee: emp, record: record || null })}
                      >
                        {record?.check_in?.slice(0, 5) || <span className="text-outline-variant text-xs italic">—</span>}
                      </span>
                    </td>

                    {/* Salida (clicable) */}
                    <td className="px-4 py-4">
                      <span
                        className="text-sm font-mono cursor-pointer hover:text-primary hover:underline"
                        onClick={() => setEditModal({ employee: emp, record: record || null })}
                      >
                        {record?.check_out?.slice(0, 5) || <span className="text-outline-variant text-xs italic">—</span>}
                      </span>
                    </td>

                    {/* Horas */}
                    <td className="px-4 py-4 text-sm font-semibold tabular-nums">
                      {record?.hours_worked ? `${record.hours_worked}h` : '—'}
                    </td>

                    {/* Tardanza */}
                    <td className="px-4 py-4 text-sm tabular-nums">
                      {(record?.late_minutes || 0) > 0
                        ? <span className="text-amber-600 font-bold">{record!.late_minutes} min</span>
                        : '—'}
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        {isLoading ? (
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            {!record?.check_in && (
                              <button
                                onClick={() => registerCheckIn(emp)}
                                className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-bold hover:bg-green-100 transition-colors"
                              >
                                Entrada
                              </button>
                            )}
                            {record?.check_in && !record?.check_out && (
                              <button
                                onClick={() => registerCheckOut(emp)}
                                className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-bold hover:bg-blue-100 transition-colors"
                              >
                                Salida
                              </button>
                            )}
                            <button
                              onClick={() => setEditModal({ employee: emp, record: record || null })}
                              className="p-1.5 text-on-surface-variant hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                              title="Editar hora manualmente"
                            >
                              <span className="material-symbols-outlined text-[16px]">edit</span>
                            </button>
                            <select
                              value={record?.status || ''}
                              onChange={e => setStatus(emp, e.target.value as AttendanceStatus)}
                              className="text-xs border border-outline-variant/30 rounded px-1.5 py-1 bg-surface-container-low outline-none focus:ring-1 focus:ring-primary"
                            >
                              <option value="">Marcar...</option>
                              {(Object.keys(STATUS_LABELS) as AttendanceStatus[]).map(s => (
                                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                              ))}
                            </select>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
